import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TextareaAutosize from "react-textarea-autosize";
import toast from "react-hot-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import PopoverHeader from "@/components/ui/popover-header";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Text from "@/components/ui/text";
import { StickyNote, StickyNotePlus } from "@dreamhub/designsystem/icons";
import { useUsers } from "@/entities/users/hooks";
import { useUpdateRevenueItemNote } from "@/pages/contracts/hooks";

// Mirrors the server's limit on revenue_items.note.
export const SERVICE_NOTE_MAX_LENGTH = 8192;

// The note's text area, shared by this popover and the service drawer.
export const SERVICE_NOTE_TEXTAREA_CLASS =
  "w-full text-base text-primary placeholder:text-tertiary px-3 py-[6px] bg-surface-field-default border-[0.5px] border-border-field-default rounded-lg resize-y shadow-field transition-all duration-200 ease-in-out hover:border-border-field-hover focus:outline-none focus:border-border-field-focus focus:bg-surface-focus-selected focus:shadow-field-focus";

interface ServiceNotePopoverProps {
  contractId: string;
  revenueItemId: string;
  note: string | null | undefined;
  noteUpdatedBy: string | null | undefined;
  // The note can be read but not edited from here (e.g. the update-terms draft,
  // where notes are edited through the service drawer instead).
  readOnly?: boolean;
  // The note is editable in principle but not right now (financials locked).
  editDisabled?: boolean;
  editDisabledTooltipText?: string;
}

const ServiceNotePopoverContent: React.FC<ServiceNotePopoverProps> = ({
  contractId,
  revenueItemId,
  note,
  noteUpdatedBy,
  readOnly = false,
  editDisabled = false,
  editDisabledTooltipText
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftNote, setDraftNote] = useState("");
  // The note just saved, held until the `note` prop moves off the value it had
  // at save time: the Services table only learns of a save from its refetch.
  const [savedNote, setSavedNote] = useState<{
    value: string | null;
    propAtSave: string | null | undefined;
  } | null>(null);
  const updateNoteMutation = useUpdateRevenueItemNote();
  const shownNote =
    savedNote && note === savedNote.propAtSave ? savedNote.value : note;
  // A row without a note offers to add one, straight into the editor.
  const isAdding = !shownNote;

  const authorIds = useMemo(
    () => (noteUpdatedBy ? [noteUpdatedBy] : []),
    [noteUpdatedBy]
  );
  const { users } = useUsers(authorIds, { enabled: isOpen && !!noteUpdatedBy });
  // useUsers stands in an "Unknown" user until the author loads; show no
  // author line rather than that placeholder.
  const author = users[0];
  const authorName =
    author && author.id === noteUpdatedBy ? author.displayName : undefined;

  // Once the prop has caught up, it is the source again.
  useEffect(() => {
    if (savedNote && note !== savedNote.propAtSave) setSavedNote(null);
  }, [note, savedNote]);

  // The lock can land while the editor is open; drop back to reading, or
  // close when adding, since there is no note to read.
  useEffect(() => {
    if (!editDisabled) return;
    setIsEditing(false);
    if (isAdding) setIsOpen(false);
  }, [editDisabled, isAdding]);

  if (isAdding && readOnly) return null;

  // A save in flight holds the popover open, so a failed save keeps the draft.
  const isSaving = updateNoteMutation.isPending;

  const closePopover = () => {
    setIsOpen(false);
    setIsEditing(false);
  };

  // Implicit closes (outside click, the trigger again) only close an add with
  // nothing typed, so a note being added is not lost.
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setIsOpen(true);
      if (isAdding) {
        setDraftNote("");
        setIsEditing(true);
      }
      return;
    }
    if (isSaving || (isAdding && draftNote.trim())) return;
    closePopover();
  };

  // While editing a note, X, Esc and Cancel discard the draft and return to
  // reading rather than closing the popover; while adding, they close it.
  const handleClose = () => {
    if (isSaving) return;
    if (isEditing && !isAdding) {
      setIsEditing(false);
      return;
    }
    closePopover();
  };

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    event.preventDefault();
    handleClose();
  };

  const startEditing = () => {
    setDraftNote(shownNote ?? "");
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = draftNote.trim();
    const nextNote = trimmed ? draftNote : null;
    const propAtSave = note;
    updateNoteMutation.mutate(
      {
        pathParams: { contractId, revenueItemId },
        data: { note: nextNote }
      },
      {
        onSuccess: () => {
          setSavedNote({ value: nextNote, propAtSave });
          setIsEditing(false);
          // Adding or clearing a note swaps the icon the popover hangs from.
          if (isAdding || !trimmed) setIsOpen(false);
        },
        onError: () => {
          toast.error(t("contractTerms.note.saveError"));
        }
      }
    );
  };

  const canEdit = !readOnly;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {isAdding ? (
          <IconButton
            variant="iconNoBg"
            size="sm"
            iconColor="secondary"
            disabled={editDisabled}
            tooltipText={
              editDisabled
                ? editDisabledTooltipText
                : t("contractTerms.note.add")
            }
            aria-label={t("contractTerms.note.add")}
            data-test={`service-note-add-${revenueItemId}`}
          >
            <StickyNotePlus />
          </IconButton>
        ) : (
          <IconButton
            variant="iconNoBg"
            size="sm"
            iconColor="secondary"
            aria-label={t("contractTerms.note.open")}
            data-test={`service-note-trigger-${revenueItemId}`}
          >
            <StickyNote />
          </IconButton>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0"
        onEscapeKeyDown={handleEscapeKeyDown}
        data-test={`service-note-popover-${revenueItemId}`}
      >
        <PopoverHeader
          title={t("contractTerms.note.title")}
          onClose={handleClose}
          actions={
            canEdit && !isEditing ? (
              <Button
                variant="tertiary"
                onClick={startEditing}
                disabled={editDisabled}
                tooltipText={editDisabled ? editDisabledTooltipText : undefined}
                data-test={`service-note-edit-${revenueItemId}`}
              >
                {t("contractTerms.table.edit")}
              </Button>
            ) : null
          }
        />
        <div className="flex flex-col gap-4 p-4">
          {isEditing ? (
            <div className="flex flex-col gap-1">
              <TextareaAutosize
                value={draftNote}
                onChange={(event) => setDraftNote(event.target.value)}
                maxLength={SERVICE_NOTE_MAX_LENGTH}
                minRows={3}
                maxRows={12}
                autoFocus
                placeholder={t(
                  isAdding
                    ? "contractTerms.note.addPlaceholder"
                    : "contractTerms.note.placeholder"
                )}
                className={SERVICE_NOTE_TEXTAREA_CLASS}
                aria-label={t("contractTerms.note.title")}
                data-test={`service-note-input-${revenueItemId}`}
              />
              <div className="flex justify-end gap-1">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={updateNoteMutation.isPending}
                  data-test={`service-note-cancel-${revenueItemId}`}
                >
                  {t("contractTerms.cancel")}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={
                    updateNoteMutation.isPending ||
                    (isAdding ? !draftNote.trim() : draftNote === shownNote)
                  }
                  data-test={`service-note-save-${revenueItemId}`}
                >
                  {t("contractTerms.save")}
                </Button>
              </div>
            </div>
          ) : (
            <Text
              variant="body"
              className="whitespace-pre-wrap break-words max-h-72 overflow-y-auto"
              data-test={`service-note-text-${revenueItemId}`}
            >
              {shownNote}
            </Text>
          )}
          {authorName && !isAdding ? (
            <Text
              variant="smBodySecondary"
              data-test={`service-note-author-${revenueItemId}`}
            >
              {t("contractTerms.note.byUser", { name: authorName })}
            </Text>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// The note icon for a service row and the popover it opens. The popover saves
// straight to the revenue item: no terms version, no history. A row with no
// note shows an add icon instead, or nothing when read-only. Keyed by item, so
// a table row reused for another service starts with fresh state.
const ServiceNotePopover: React.FC<ServiceNotePopoverProps> = (props) => (
  <ServiceNotePopoverContent key={props.revenueItemId} {...props} />
);

export default ServiceNotePopover;
